import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { useTenantMutation } from '@/hooks/useTenantMutation';
import { toast } from '@/utils/toast';
import type { TaskWithDetails, PaginatedTasks, TaskPriority } from '@/types/task';
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_COLORS, PRIORITY_LABELS } from '@/types/task';

import FaIcon from '@/components/common/FaIcon';
import Alert from '@/components/common/Alert';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import Button, { IconButton } from '@/components/common/Button';
import Input from '@/components/common/Input';
import MetricValue from '@/components/common/MetricValue';
import { SEVERITY_SPINE, type Severity } from '@/components/common/severity';

// Task priority → operational visual-weight scale
const PRIORITY_RANK: Record<TaskPriority, Severity> = {
  urgent: 'critical',
  high: 'warning',
  medium: 'elevated',
  low: 'calm',
};

interface TeamMember {
  id: string;
  name: string;
  email: string;
}

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

const PRIORITY_SEGMENT_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200',
  medium: 'bg-primary-100 text-primary-800 dark:bg-primary-800 dark:text-primary-200',
  high: 'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200',
};

const PRIORITY_SEGMENT_INACTIVE = 'text-theme-tertiary';

export default function FieldTasks() {
  const navigate = useNavigate();
  const { activeIncident } = useIncidentStore();
  const [showMyTasks, setShowMyTasks] = useState(true);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [taskPage, setTaskPage] = useState(1);
  const [allTasks, setAllTasks] = useState<TaskWithDetails[]>([]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setTaskPage(1);
  }, [showMyTasks, activeIncident?.id]);

  const taskParams = useMemo(() => {
    const p: Record<string, string> = { limit: '50', page: String(taskPage) };
    if (showMyTasks) p.my_tasks = 'true';
    if (activeIncident?.id) p.incident_id = activeIncident.id;
    return p;
  }, [activeIncident?.id, showMyTasks, taskPage]);

  const {
    data: taskData,
    isLoading,
    isError: isTasksError,
    refetch: refetchTasks,
  } = useTenantQuery<PaginatedTasks>(
    ['field-tasks', taskParams, showMyTasks],
    '/tasks',
    { params: taskParams }
  );

  const pageTasks: TaskWithDetails[] = taskData?.data || [];
  const totalTasks = taskData?.total ?? allTasks.length;

  // Merge paginated results — reset when filters change (page 1), append on Load More
  useEffect(() => {
    if (taskPage === 1) {
      setAllTasks(pageTasks);
    } else {
      setAllTasks((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        return [...prev, ...pageTasks.filter((t) => !existingIds.has(t.id))];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskData]);

  const tasks = allTasks;

  const { data: teamData } = useTenantQuery<{ data: TeamMember[] } | TeamMember[]>(
    ['team-members'],
    '/team/members',
    { enabled: showQuickAdd }
  );

  useEffect(() => {
    if (teamData) {
      const members = Array.isArray(teamData) ? teamData : teamData.data;
      setTeamMembers(members);
    }
  }, [teamData]);

  useEffect(() => {
    if (showQuickAdd) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [showQuickAdd]);

  const createTaskMutation = useTenantMutation<unknown, { title: string; priority: TaskPriority; assigned_to?: string; incident_id: string }>(
    '/tasks',
    'POST',
    {
      invalidateKeys: [['field-tasks']],
      showSuccessToast: true,
      successMessage: 'Task created',
      onSuccess: () => {
        resetForm();
        setShowQuickAdd(false);
      },
    }
  );

  const resetForm = () => {
    setTitle('');
    setPriority('medium');
    setAssignedTo('');
  };

  const handleQuickCreate = () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!activeIncident) {
      toast.error('No active incident selected');
      return;
    }
    createTaskMutation.mutate({
      title: title.trim(),
      priority,
      assigned_to: assignedTo || undefined,
      incident_id: activeIncident.id,
    });
  };

  if (isLoading) {
    return <LoadingState message="Loading tasks..." />;
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="heading-3">{showMyTasks ? 'My Tasks' : 'All Tasks'}</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-theme-primary">
            <Button
              variant={showMyTasks ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setShowMyTasks(true)}
              className="rounded-none border-0"
            >
              Mine
            </Button>
            <Button
              variant={!showMyTasks ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setShowMyTasks(false)}
              className="rounded-none border-0"
            >
              All
            </Button>
          </div>
          <span className="text-sm text-theme-tertiary tabular-nums">
            {totalTasks > tasks.length ? (
              <><MetricValue value={tasks.length} className="font-semibold" /> of <MetricValue value={totalTasks} /></>
            ) : (
              <MetricValue value={tasks.length} className="font-semibold" />
            )}
          </span>
          <IconButton icon={<FaIcon icon="arrows-rotate" className="h-4 w-4" />} label="Refresh tasks" onClick={() => { setTaskPage(1); refetchTasks(); }} variant="ghost" size="sm" />
          <button
            onClick={() => setShowQuickAdd((prev) => !prev)}
            className={`flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] rounded-full touch-manipulation ${
              showQuickAdd
                ? 'bg-gray-300 dark:bg-gray-600 text-theme-secondary active:bg-gray-400'
                : 'bg-primary-600 text-white active:bg-primary-700'
            }`}
            aria-label={showQuickAdd ? 'Close quick add' : 'Quick add task'}
            aria-expanded={showQuickAdd}
          >
            <FaIcon icon="plus" className={`h-5 w-5 transition-transform ${showQuickAdd ? 'rotate-45' : ''}`} />
          </button>
        </div>
      </div>

      {/* Inline Quick Add Form */}
      {showQuickAdd && (
        <div className="bg-surface-primary rounded-lg border border-primary-200 dark:border-primary-800 p-3 space-y-3">
          <Input aria-label="Task title"
            ref={titleInputRef as React.Ref<HTMLInputElement>}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleQuickCreate();
              }
            }}
          />

          {/* Priority segmented control */}
          <div>
            <span className="block text-xs font-medium text-theme-tertiary mb-1">Priority</span>
            <div className="grid grid-cols-4 gap-1 bg-surface-inset rounded-lg p-1">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  className={`min-h-[40px] rounded-md text-xs font-medium touch-manipulation transition-colors ${
                    priority === p ? PRIORITY_SEGMENT_COLORS[p] : PRIORITY_SEGMENT_INACTIVE
                  }`}
                >
                  {PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Assignee dropdown */}
          <Input aria-label="Assign to"
            as="select"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value="">Unassigned</option>
            {teamMembers.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </Input>

          {/* Submit */}
          <Button
            onClick={handleQuickCreate}
            disabled={createTaskMutation.isPending || !title.trim()}
            variant="primary"
            fullWidth
            loading={createTaskMutation.isPending}
          >
            {createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
          </Button>
        </div>
      )}

      {isTasksError && (
        <Alert variant="danger" title="Failed to load tasks">
          Unable to load tasks. Please check your connection and try again.
        </Alert>
      )}

      {!isTasksError && tasks.length === 0 ? (
        <EmptyState
          variant="operational"
          icon="clipboard-list"
          title={showMyTasks ? 'No tasks assigned to you' : 'No tasks found'}
          description={showMyTasks ? 'Tasks assigned to you will appear here.' : 'No tasks exist for this incident yet.'}
        />
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const isOverdue = t.sla_status === 'critical' || t.sla_status === 'overdue' || (!!t.due_at && new Date(t.due_at) < new Date());
            const rank: Severity = isOverdue ? 'critical' : PRIORITY_RANK[t.priority] ?? 'calm';
            return (
            <button
              key={t.id}
              onClick={() => navigate(`/field/tasks/${t.id}`)}
              className={`w-full text-left bg-surface-primary rounded-lg border border-theme-primary p-3 min-h-[44px] touch-manipulation active:bg-surface-secondary ${SEVERITY_SPINE[rank]}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {isOverdue && (
                      <span className="inline-block h-2 w-2 rounded-full bg-red-500 shrink-0 animate-pulse" title="Overdue or SLA Critical" />
                    )}
                    <p className="text-sm font-semibold text-theme-primary truncate">{t.title}</p>
                  </div>
                  <p className="text-xs text-theme-tertiary mt-0.5">
                    <MetricValue value={`#${t.task_number}`} />
                    {t.location_name && <> &middot; {t.location_name}</>}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                    {STATUS_LABELS[t.status]}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_COLORS[t.priority]}`}>
                    {PRIORITY_LABELS[t.priority]}
                  </span>
                </div>
              </div>
            </button>
          );})}
          {tasks.length < totalTasks && (
            <Button
              onClick={() => setTaskPage((p) => p + 1)}
              disabled={isLoading}
              variant="outline"
              fullWidth
              loading={isLoading}
              className="mt-2"
            >
              {isLoading ? 'Loading...' : `Load More (${totalTasks - tasks.length} remaining)`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
